import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import apiService from '../services/apiService';
import useInventoryFilters, { createInitialFilters, filterInventoryUnitsFully } from './useInventoryFilters';
import useUserPreferences from './useUserPreferences';
import { useDealershipSettings } from '../contexts/settingsContext';
import { normalizeAgingSelection } from '../utils/dealershipSettings';
import {
    MACHINE_URL_PARAMS,
    buildMachineCatalog,
    parseMachineParams,
    sanitizeMachineFilters,
    writeMachineParams,
} from '../utils/machineFilters';
import {
    DRILL_PARAMS,
    branchParamMatches,
    dropStaleDrillParams,
    locationList,
    parseDrillParams,
    resolveBranchParam,
    toLocationValue,
} from '../utils/inventoryView';

/**
 * The inventory page's state and behaviour, shared by the current page
 * (InventoryDashboard) and the redesigned one (InventoryPageV2) so both run
 * the exact same logic: the inventory and open-task loads, the filters and
 * their saved preferences (hydration guard, legacy migration, auto-save),
 * the URL params (?branch=, ?make/model/year/noyear, and with `v2Links` the
 * multi-branch and drill-down params of decision D14), the anomaly
 * selection, the Open Tasks toggle, the global clear, and which unit the
 * detail pane shows (including the Task Board and Branches entry points).
 *
 * `v2Links` (the new design only) turns on:
 * - ?branch=A,B → several locations (a single ?branch=X works as today);
 * - ?aging=, ?risk=, ?interest=, ?signal= → those filters, applied once the
 *   data is in and counting as an interaction (they beat saved prefs, like
 *   ?branch=); each param is dropped from the URL once its filter changes;
 * - ?committed=1 → "Include committed units" on (never saved in prefs): the
 *   committed units are loaded once per session and returned apart, as
 *   `committedRows` (filtered like the inventory), for the grid only.
 */
export default function useInventoryPage({ isAuthenticated = false, v2Links = false, restore = null } = {}) {
    const routerLocation = useLocation();
    const navigate = useNavigate();
    const dataContext = useData();
    const {
        inventoryData, inventoryLoading: loading, inventoryError: error, loadInventory,
        committedData, committedLoading = false, committedError = null, loadCommitted,
    } = dataContext;
    // Stable while the inventory is unchanged, so the memos below hold.
    const allData = useMemo(() => inventoryData?.units || [], [inventoryData]);
    // The dealership's aging buckets: filters, saved prefs and ?aging= hold
    // bucket ids 1–4; legacy labels and aliases map to them.
    const { aging } = useDealershipSettings();
    // `restore` (v2): the view the user left for the unit page, back as it
    // was (D7): filters, signals and Open Tasks. It counts as an interaction,
    // so saved preferences arriving later do not replace it.
    const [anomalyFilter, setAnomalyFilter] = useState(() => new Set(restore?.anomaly || []));
    const {
        filters, rawFilters, setFilters, filteredData, machineBaseData, stats,
    } = useInventoryFilters(
        allData,
        anomalyFilter,
        restore?.filters ? { ...restore.filters, agingBucket: normalizeAgingSelection(restore.filters.agingBucket, aging.buckets) } : null,
    );
    // Make / Model / Year options and labels for this dealership's units.
    const machineCatalog = useMemo(() => buildMachineCatalog(allData), [allData]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [initialTab, setInitialTab] = useState('ai');
    // Open the create-task form when the pane opens (v2 "Create task").
    const [createTaskOnOpen, setCreateTaskOnOpen] = useState(false);
    const [openTaskStocks, setOpenTaskStocks] = useState(null);
    const [taskFilterActive, setTaskFilterActive] = useState(Boolean(restore?.taskFilterActive));
    // "Include committed units" (v2): from ?committed=1, never saved.
    const [committedOn, setCommittedOn] = useState(
        () => v2Links && new URLSearchParams(routerLocation.search).get('committed') === '1',
    );

    // Track whether the user has manually changed filters since page load,
    // so we don't clobber their active session when prefs arrive asynchronously.
    const userHasInteracted = useRef(Boolean(restore));

    // Machine-filter URL sync: set once the params have been read on load;
    // pending holds values read from the URL until they reach the filters.
    const machineUrlReadyRef = useRef(false);
    const pendingUrlMachineRef = useRef(null);
    // Drill-down params (v2): the values read from the URL until they reach
    // the filters; only then may a mismatch drop a param.
    const drillReadyRef = useRef(false);
    const pendingDrillRef = useRef(null);

    // User preferences — auto-saved via debounce, hydrated on login
    const { hasLoadedPrefs, savedPrefs, queueSave } = useUserPreferences(isAuthenticated);

    // Track whether initial hydration from saved prefs has completed, so we
    // know when it's safe to auto-save subsequent user changes.
    const hasHydratedRef = useRef(false);

    useEffect(() => { loadInventory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch stock numbers with open tasks for the filter badge
    useEffect(() => {
        apiService.getActivities({ status: 'open' })
            .then(data => {
                const stocks = new Set((data.activities || []).map(a => String(a.stock_number)));
                // Also include in_progress
                return apiService.getActivities({ status: 'in_progress' }).then(data2 => {
                    (data2.activities || []).forEach(a => stocks.add(String(a.stock_number)));
                    setOpenTaskStocks(stocks);
                });
            })
            .catch(() => setOpenTaskStocks(new Set()));
    }, []);

    // Apply saved preferences once they've loaded — but only if the user hasn't
    // already touched the filters during this session (e.g. via URL param or
    // manual interaction). This prevents prefs from clobbering an active view.
    useEffect(() => {
        if (!hasLoadedPrefs) return;
        if (userHasInteracted.current) {
            hasHydratedRef.current = true;
            return;
        }

        if (savedPrefs) {
            const { filters: savedFilters, anomalyChips } = savedPrefs;
            // Migrate legacy agingBucket prefs: it was a single string ('All' or one
            // bucket) before the multi-select change; the current shape is an array.
            // A stale string here would spread over the [] default and, being truthy,
            // pass the length>0 guard while String.includes matches nothing — emptying
            // the table. Coerce: 'All' → [], any other single bucket → [bucket].
            // Since dealership settings, the buckets are saved as ids 1–4: a
            // legacy label ('91–180 days') reads as its id, and the next save
            // stores the id.
            const migrated = { ...savedFilters };
            if ('agingBucket' in migrated) {
                migrated.agingBucket = normalizeAgingSelection(migrated.agingBucket, aging.buckets);
            }
            // Several locations (the new design) are saved as a list; keep the
            // single-branch string shape for one, 'All' for none.
            if (Array.isArray(migrated.location)) {
                migrated.location = toLocationValue(locationList(migrated.location));
            }
            // Make / Model / Year: prefs saved before these filters existed lack
            // the keys; anything malformed falls back to the default.
            Object.assign(migrated, sanitizeMachineFilters(savedFilters));
            setFilters((prev) => ({ ...prev, ...migrated }));
            if (Array.isArray(anomalyChips) && anomalyChips.length > 0) {
                setAnomalyFilter(new Set(anomalyChips));
            }
        }

        // Mark hydration complete — from here on, any filter/anomaly change
        // triggers a debounced auto-save in the effect below.
        hasHydratedRef.current = true;
    }, [hasLoadedPrefs, savedPrefs]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save filters + anomaly chips whenever they change (post-hydration).
    // useUserPreferences debounces internally, so rapid changes coalesce into
    // a single write. `search` is explicitly excluded from the saved state.
    // The raw filters are saved: makes/models the current inventory lacks are
    // hidden on screen but kept in prefs until the user changes the selection
    // (any change made through the controls starts from the visible state).
    useEffect(() => {
        if (!isAuthenticated) return;
        if (!hasHydratedRef.current) return;
        const { search: _search, ...filtersToSave } = rawFilters;
        queueSave(filtersToSave, Array.from(anomalyFilter));
    }, [rawFilters, anomalyFilter, isAuthenticated, queueSave]);

    // Focus target after a global clear. The clear buttons disappear once
    // nothing is active, so focus lands here instead of falling to <body>.
    const resultCountRef = useRef(null);

    // The query string this page last navigated to, until the router publishes
    // it. The router updates its location a beat after navigate(), so a render
    // in between still sees the old query; building the next URL from that
    // would write already-removed params back (e.g. ?branch= after a clear).
    // It is dropped once the router shows it — or shows a URL this page did not
    // write (back/forward), which then is the truth.
    const pendingSearchRef = useRef(null);
    const pendingTrailRef = useRef({ stale: null, written: new Set() });
    if (pendingSearchRef.current !== null) {
        const { stale, written } = pendingTrailRef.current;
        const seen = routerLocation.search;
        if (seen === pendingSearchRef.current || (seen !== stale && !written.has(seen))) {
            pendingSearchRef.current = null;
        }
    }
    const currentSearch = () => pendingSearchRef.current ?? routerLocation.search;

    // Rewrite the query string in place: replaces the entry (no new history
    // step), keeps every param the mutator leaves alone, and does nothing when
    // the string would not change.
    const replaceParams = useCallback((mutate) => {
        const params = new URLSearchParams(pendingSearchRef.current ?? routerLocation.search);
        const before = params.toString();
        mutate(params);
        const search = params.toString();
        if (search === before) return;
        if (pendingSearchRef.current === null) {
            pendingTrailRef.current = { stale: routerLocation.search, written: new Set() };
        }
        pendingSearchRef.current = search ? `?${search}` : '';
        pendingTrailRef.current.written.add(pendingSearchRef.current);
        navigate(
            { pathname: routerLocation.pathname, search: pendingSearchRef.current, hash: routerLocation.hash },
            { replace: true, state: routerLocation.state },
        );
    }, [routerLocation, navigate]);

    // Drop ?branch= from the URL once the user has moved off that branch, so a
    // reload does not bring it back.
    const dropBranchParam = useCallback(() => {
        replaceParams((params) => params.delete('branch'));
    }, [replaceParams]);

    // Mark that the user has actively changed filters so the pref-apply effect
    // will not fire again if prefs arrive late.
    const handleFiltersChange = useCallback((newFilters) => {
        userHasInteracted.current = true;
        const branchParam = new URLSearchParams(currentSearch()).get('branch');
        if (branchParam && !branchParamMatches(newFilters.location, branchParam, { multi: v2Links })) {
            dropBranchParam();
        }
        setFilters(newFilters);
    }, [setFilters, routerLocation.search, dropBranchParam, v2Links]); // eslint-disable-line react-hooks/exhaustive-deps

    // A filter change made from a control that never touches the location
    // (KPI buckets, the opportunity toggles): today's functional updates.
    const updateFilters = useCallback((patchOrFn) => {
        userHasInteracted.current = true;
        setFilters((prev) => (typeof patchOrFn === 'function' ? patchOrFn(prev) : { ...prev, ...patchOrFn }));
    }, [setFilters]);

    const handleAnomalyVerdictToggle = useCallback((verdict) => {
        userHasInteracted.current = true;
        setAnomalyFilter((prev) => {
            const next = new Set(prev);
            next.has(verdict) ? next.delete(verdict) : next.add(verdict);
            return next;
        });
    }, []);

    const handleAnomalyGroupToggle = useCallback((verdicts, activate) => {
        userHasInteracted.current = true;
        setAnomalyFilter((prev) => {
            const next = new Set(prev);
            verdicts.forEach((v) => (activate ? next.add(v) : next.delete(v)));
            return next;
        });
    }, []);

    const handleAnomalyClearSignals = useCallback(() => {
        userHasInteracted.current = true;
        setAnomalyFilter(new Set());
    }, []);

    // Replace the whole anomaly selection (v2 "Needs attention: unit issues").
    const setAnomalySelection = useCallback((verdicts) => {
        userHasInteracted.current = true;
        setAnomalyFilter(new Set(verdicts));
    }, []);

    // The one global "Clear all filters" action: every filter back to its
    // default, no anomaly signals and Open Tasks off. Every page-wide clear
    // entry point routes through here. The auto-save effect persists the
    // cleared state on the next debounce tick.
    const handleClearAllFilters = useCallback(() => {
        userHasInteracted.current = true;
        setFilters(createInitialFilters());
        setAnomalyFilter(new Set());
        setTaskFilterActive(false);
        // One URL write for both ?branch= and the machine params.
        replaceParams((params) => {
            params.delete('branch');
            MACHINE_URL_PARAMS.forEach((p) => params.delete(p));
            if (v2Links) DRILL_PARAMS.forEach((p) => params.delete(p));
        });
        resultCountRef.current?.focus();
    }, [setFilters, replaceParams, v2Links]);

    // The rows the table actually shows: the filtered set, narrowed further by
    // the Open Tasks toggle. Both the table and the "X of Y units" count read
    // this one list so they cannot drift apart.
    const displayedData = taskFilterActive && openTaskStocks
        ? filteredData.filter(u => openTaskStocks.has(String(u.StockNumber)))
        : filteredData;

    // Committed units (v2 toggle): the same plain filters as the inventory,
    // and never a unit the regular inventory already has. They feed the grid
    // only: stats, KPIs, signals and Needs attention all read allData /
    // filteredData / displayedData, which never include them. A KPI / Needs
    // attention drill-down (aging, risk, interest, signals, opportunities:
    // `hasInStockOnlyFilter`, and Open Tasks, counted on this lot only) lists
    // no committed row, so the rows shown equal the number clicked (QA E1).
    const committedUnits = useMemo(() => {
        const units = committedData?.units || [];
        if (!units.length) return units;
        const known = new Set(allData.map((u) => String(u.StockNumber)));
        return units.filter((u) => !known.has(String(u.StockNumber)));
    }, [committedData, allData]);
    const committedRows = useMemo(() => {
        if (!committedOn || !committedUnits.length || taskFilterActive) return [];
        return filterInventoryUnitsFully(committedUnits, filters, anomalyFilter, aging, { committed: true });
    }, [committedOn, committedUnits, filters, anomalyFilter, aging, taskFilterActive]);
    const committedTotal = committedData ? (committedData.count ?? committedUnits.length) : null;

    // Also after DataContext dropped the list (Refresh, new dealership
    // settings): loaded again under the settings now in use (QA P2-1).
    const committedPresent = Boolean(committedData);
    useEffect(() => {
        if (committedOn && !committedPresent) loadCommitted?.();
    }, [committedOn, committedPresent]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleCommitted = useCallback(() => {
        const next = !committedOn;
        setCommittedOn(next);
        replaceParams((params) => {
            if (next) params.set('committed', '1');
            else params.delete('committed');
        });
    }, [committedOn, replaceParams]);

    // What the machine panel counts from: every other filter, Open Tasks too.
    const machineFacetData = useMemo(() => (
        taskFilterActive && openTaskStocks
            ? machineBaseData.filter(u => openTaskStocks.has(String(u.StockNumber)))
            : machineBaseData
    ), [taskFilterActive, openTaskStocks, machineBaseData]);

    // Filtered units with an open task: the Open Tasks toggle's count.
    const openTaskCount = openTaskStocks && filteredData
        ? filteredData.filter(u => openTaskStocks.has(String(u.StockNumber))).length
        : 0;

    // Whether anything is narrowing the table, i.e. whether the global
    // clear has something to do.
    const hasActiveFilters = anomalyFilter.size > 0
        || taskFilterActive
        || Object.entries(createInitialFilters()).some(([key, initial]) => (
            Array.isArray(initial)
                ? (filters[key] || []).length > 0
                : (filters[key] ?? initial) !== initial
        ));

    // Apply ?branch= URL param once data is loaded
    useEffect(() => {
        if (!allData.length) return;
        const params      = new URLSearchParams(routerLocation.search);
        const branchParam = params.get('branch');
        if (branchParam) {
            // Navigating in with an explicit branch counts as an interaction, so
            // the saved-prefs hydration below does NOT clobber it back to 'All'.
            // (The inventory is already in context from the branch page, so this
            // effect fires on mount before prefs resolve — without the flag the
            // async prefs load overwrites the branch filter.)
            userHasInteracted.current = true;
            const match = resolveBranchParam(branchParam, allData, { multi: v2Links });
            if (match) {
                setFilters(prev => ({ ...prev, location: match }));
            }
        }

        // ?make= / ?model= / ?year= / ?noyear= — like ?branch=, a link that
        // carries them counts as an interaction so saved prefs do not win.
        const machine = parseMachineParams(routerLocation.search, machineCatalog);
        if (machine) {
            userHasInteracted.current = true;
            pendingUrlMachineRef.current = machine;
            setFilters(prev => ({ ...prev, ...machine }));
        }
        machineUrlReadyRef.current = true;

        // ?aging= / ?risk= / ?interest= / ?signal= (v2): the same contract.
        if (v2Links) {
            const drill = parseDrillParams(routerLocation.search, aging.buckets);
            if (drill) {
                userHasInteracted.current = true;
                const { verdicts, ...drillFilters } = drill;
                pendingDrillRef.current = drill;
                if (Object.keys(drillFilters).length) setFilters(prev => ({ ...prev, ...drillFilters }));
                if (verdicts) setAnomalyFilter(new Set(verdicts));
            }
            drillReadyRef.current = true;
        }
    }, [allData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep ?make= / ?model= / ?year= / ?noyear= in step with the filters.
    // Waits until the URL has been read (and its values applied), so the
    // incoming params are never overwritten by the defaults.
    useEffect(() => {
        if (!machineUrlReadyRef.current) return;
        const pending = pendingUrlMachineRef.current;
        if (pending) {
            const applied = Object.entries(pending).every(
                ([key, value]) => JSON.stringify(filters[key]) === JSON.stringify(value),
            );
            if (!applied) return;
            pendingUrlMachineRef.current = null;
        }
        replaceParams((params) => writeMachineParams(params, filters, machineCatalog));
    }, [filters.makes, filters.models, filters.yearFrom, filters.yearTo, filters.includeNoYear, machineCatalog]); // eslint-disable-line react-hooks/exhaustive-deps

    // Drop a drill-down param once its filter says something else (v2).
    useEffect(() => {
        if (!v2Links || !drillReadyRef.current) return;
        const pending = pendingDrillRef.current;
        if (pending) {
            const applied = Object.entries(pending).every(([key, value]) => (
                key === 'verdicts'
                    ? value.length === anomalyFilter.size && value.every((v) => anomalyFilter.has(v))
                    : JSON.stringify(filters[key]) === JSON.stringify(value)
            ));
            if (!applied) return;
            pendingDrillRef.current = null;
        }
        replaceParams((params) => dropStaleDrillParams(params, filters, anomalyFilter, aging.buckets));
    }, [filters.agingBucket, filters.riskTier, filters.buyerHeat, anomalyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Open unit detail pane from sessionStorage (set by TaskBoard)
    // Runs on every render cycle to catch the pending open regardless of when data loads
    const pendingProcessed = useRef(false);
    useEffect(() => {
        const pending = sessionStorage.getItem('pendingOpenUnit');
        if (!pending) {
            pendingProcessed.current = false;
            return;
        }
        if (pendingProcessed.current) return;
        if (allData.length === 0) return;

        pendingProcessed.current = true;
        sessionStorage.removeItem('pendingOpenUnit');
        try {
            const { stockNumber, tab } = JSON.parse(pending);
            const fullUnit = allData.find(u => String(u.StockNumber) === String(stockNumber));
            if (fullUnit) {
                setSelectedUnit(fullUnit);
                setInitialTab(tab || 'activity');
                setCreateTaskOnOpen(false);
            }
        } catch (e) { /* ignore */ }
    });

    // Open unit from router state (from branch overview)
    useEffect(() => {
        if (allData.length === 0) return;
        const state = routerLocation.state;
        if (!state?.openUnit) return;

        const stockNum = state.openUnit.StockNumber || state.openUnit.stock_number;
        if (state.openUnit.MAKE) {
            setSelectedUnit(state.openUnit);
            if (state.openTab) setInitialTab(state.openTab);
        } else if (stockNum) {
            const fullUnit = allData.find(u => String(u.StockNumber) === String(stockNum));
            if (fullUnit) {
                setSelectedUnit(fullUnit);
                if (state.openTab) setInitialTab(state.openTab);
            }
        }
        navigate('.', { replace: true, state: {} });
    }, [allData]); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = useCallback(() => loadInventory(true), [loadInventory]);

    // Like the controls, the KPI buckets start from the visible filters.
    // `bucket`: a bucket id (1–4).
    const handleAgingClick = useCallback((bucket) => {
        userHasInteracted.current = true;
        const current = filters.agingBucket || [];
        const next = current.includes(bucket)
            ? current.filter((b) => b !== bucket)
            : [...current, bucket];
        setFilters({ ...filters, agingBucket: next });
    }, [filters, setFilters]);

    const handleRiskTierClick = useCallback((tier) => {
        userHasInteracted.current = true;
        setFilters({ ...filters, riskTier: tier });
    }, [filters, setFilters]);

    const toggleQualifyingQuote = useCallback(() => {
        updateFilters((prev) => ({ ...prev, qualifyingQuote: !prev.qualifyingQuote }));
    }, [updateFilters]);

    const toggleRetargeting = useCallback(() => {
        updateFilters((prev) => ({ ...prev, retargeting: !prev.retargeting }));
    }, [updateFilters]);

    const toggleTaskFilter = useCallback(() => setTaskFilterActive((v) => !v), []);

    // Open the unit detail pane on a tab ('ai' by default, as a row click
    // does today); `createTask` also opens its create-request form.
    const openUnit = useCallback((unit, tab, { createTask = false } = {}) => {
        setSelectedUnit(unit);
        setInitialTab(tab || 'ai');
        setCreateTaskOnOpen(createTask);
    }, []);
    const closeUnit = useCallback(() => setSelectedUnit(null), []);

    // Gate the dashboard until BOTH inventory data AND saved preferences have
    // loaded. Avoids the "flash of unfiltered inventory" where all 1080 units
    // render for a split-second before the saved filters get applied.
    const waitingForPrefs = isAuthenticated && !hasLoadedPrefs;
    const showLoadingGate = (loading && !allData.length) || (waitingForPrefs && !allData.length);

    return {
        // data and load state
        allData,
        loading,
        error,
        showLoadingGate,
        refresh,
        // filters
        filters,
        rawFilters,
        filteredData,
        displayedData,
        machineFacetData,
        machineCatalog,
        stats,
        changeFilters: handleFiltersChange,
        updateFilters,
        handleAgingClick,
        handleRiskTierClick,
        toggleQualifyingQuote,
        toggleRetargeting,
        // anomaly signals
        anomalyFilter,
        handleAnomalyVerdictToggle,
        handleAnomalyGroupToggle,
        handleAnomalyClearSignals,
        setAnomalySelection,
        // open tasks
        openTaskStocks,
        openTaskCount,
        taskFilterActive,
        setTaskFilterActive,
        toggleTaskFilter,
        // committed units (v2): grid rows only
        committedOn,
        toggleCommitted,
        committedRows,
        committedUnits,
        committedTotal,
        committedLoading,
        committedError,
        // global clear
        hasActiveFilters,
        handleClearAllFilters,
        resultCountRef,
        // url
        replaceParams,
        // unit detail pane
        selectedUnit,
        initialTab,
        createTaskOnOpen,
        openUnit,
        closeUnit,
    };
}
